import {
	Component, Output, OnInit, OnChanges, SimpleChanges, OnDestroy,
	EventEmitter
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs/Rx';
import { MessageService } from 'base';
import { MonitorService } from 'monitor';
import { IpAddress, Device } from 'DiscoveryEngine.TS/Core';
import {
	MonitorPolicy, MonitorSettingsEntry, MonitorSettings_SnmpIf, MonitorSettings_SnmpIfEntry
} from 'DiscoveryEngine.TS/Monitors';
import { List, ValueEncoder, Guid, ArrayUtils } from 'DiscoveryEngine.TS/Utils';
import { MonitorConfigComponentBase } from '../MonitorConfigComponentBase';
import { IdentityService } from 'identity';
import { NavigationService } from 'netview';
import { DiscResultsService } from 'discovery';
import {
	GetDeviceResultDto, GetMonitorStateResultDto, MonitorStateDto, DisplayDeviceListEntryDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { MonitorDeviceSelectorResult } from '../monitor-device-selector/monitor-device-selector.component';
import { MonitorEntry } from 'DiscoveryEngine.TS/InfoSets/MonitorTable';


export class SnmpIfMonitorListEntry {
	public deviceGuid: string;
	public settingsEntryGuid: string;
	public deviceName: string;
	public deviceDescription: string;
	public ipAddress: string;
	public ifIndex: string;
	public ifName: string;
	public inOutMbps: string;
	public state: string;
	public stateDetails: string;
	public style: string;

	public deviceTag: Device;
	public get hasDeviceTag(): boolean {
		return !!this.deviceTag;
	}

	public ipAddressTag: IpAddress;
	public get hasIpAddressTag(): boolean {
		return !!this.ipAddressTag;
	}

	public ifIndexTag: number;
	public get hasIfIndexTag(): boolean {
		return (this.ifIndexTag !== undefined);
	}

	public settingsEntryTag: MonitorSettings_SnmpIfEntry;
	public get hasSettingsEntryTag(): boolean {
		return !!this.settingsEntryTag;
	}

	constructor(deviceGuid?: string, device?: Device, settingsEntryGuid?: string, settingsEntry?: MonitorSettings_SnmpIfEntry,
		settingsEntryState?: string, settingsEntryStateDetails?: string, settingsEntryInOutMbps?: string) {
		if (!!deviceGuid) {
			this.deviceGuid = deviceGuid;
			this.settingsEntryGuid = settingsEntryGuid;

			if (!!device) {
				this.deviceName = device.DisplayName;
				this.deviceDescription = device.DisplayDescription;
				this.ipAddress = (device.HasIpAddress ? this.ipAddress = device.IpAddress.ToString() : "");
				this.ipAddressTag = device.IpAddress;
			}
			else {
				this.deviceName = deviceGuid;
				this.deviceDescription = "";
				this.ipAddress = "";
				this.ipAddressTag = null;
			}

			this.ifIndex = "" + settingsEntry.IfIndex;
			this.ifIndexTag = settingsEntry.IfIndex;

			if (settingsEntry.HasName) {
				this.ifName = settingsEntry.Name;
			}

			if (!!settingsEntryInOutMbps) {
				this.inOutMbps = settingsEntryInOutMbps;
			}

			if (!!settingsEntryState) {
				this.state = settingsEntryState;
				if (this.state === "NoData") {
					this.state = "No Data";
				}

				this.style = "monitor-state-" + settingsEntryState.toLowerCase();
			}
			else {
				this.state = "No Data";
				this.style = "monitor-state-nodata";
			}

			if (!!settingsEntryStateDetails) {
				this.stateDetails = settingsEntryStateDetails;
			}
			else {
				this.stateDetails = "";
			}

			this.deviceTag = device;
			this.settingsEntryTag = settingsEntry;
		}
		else {
			this.deviceGuid = "";
			this.settingsEntryGuid = "";
			this.deviceName = "";
			this.deviceDescription = "";
			this.ipAddress = "";
			this.ifIndex = "";
			this.ifName = "";
			this.inOutMbps = "";
			this.state = "No Data";
			this.style = "monitor-state-nodata";
			this.stateDetails = "";

			this.deviceTag = null;
			this.ipAddressTag = null;
			this.ifIndexTag = 0;
			this.settingsEntryTag = null;
		}
	}
}

@Component({
	selector: 'snmpif-devices',
	templateUrl: './snmpifdevices.component.html',
	styleUrls: ['./snmpifdevices.component.scss']
})
export class SnmpIfDevicesComponent extends MonitorConfigComponentBase implements OnInit, OnChanges, OnDestroy {

	@Output()
	public applyTempChangesIfValid: EventEmitter<any>;

	public entries: SnmpIfMonitorListEntry[];
	public deviceSelectorVisible: boolean;
	public interfaceSelectorVisible: boolean;
	public initialInterfaceSelection: number[];

	private deviceCache: Object;
	private stateCache: Object;

	public UseHostnames: UntypedFormControl;

	private targetEntry: SnmpIfMonitorListEntry;

	constructor(private monService: MonitorService,
		private messageService: MessageService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private discResultsService: DiscResultsService,
		private fb: UntypedFormBuilder) {

		super();

		this.applyTempChangesIfValid = new EventEmitter<any>();

		this.entries = [];
		this.deviceSelectorVisible = false;
		this.interfaceSelectorVisible = false;
		this.initialInterfaceSelection = [];

		this.deviceCache = {};
		this.stateCache = {};

		this.UseHostnames = null;

		this.targetEntry = null;
	}

	public ngOnInit(): void {
		this.BuildForm();
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (this.visible) {
			this.entries = [];
			this.retrieveDevicesAndMonitorStates();
			this.BuildForm();
		}
	}

	public ngOnDestroy(): void {
		return;
	}

	public get targetDevice(): Device {
		if (this.targetEntry) {
			return this.targetEntry.deviceTag;
		}
		else {
			return null;
		}
	}

	private retrieveDevicesAndMonitorStates(): void {

		this.deviceCache = {};
		this.stateCache = {};

		if (this.editor) {
			if (this.editor.Policy.DeviceGuids.Count > 0) {
				let deviceGuidStrs = new List<string>();
				this.editor.Policy.DeviceGuids.ForEach((guid: Guid) => {
					let guidStr = ValueEncoder.EncodeGuid(guid);
					deviceGuidStrs.Add(guidStr);
				});

				this.getDevices(deviceGuidStrs).subscribe(
					(gdResult: any) => {
						this.getMonitorState(this.editor.Policy).subscribe(
							(gmsResult: any) => {
								this.rebuildEntries();
							},
							(gmsError) => {
								this.rebuildEntries();
								this.messageService.displayErrorMessage("Could not retrieve monitor state info");
							});
					},
					(gdError) => {
						this.messageService.displayErrorMessage("Could not retrieve monitor device info");
					});
			}
			else {
				this.rebuildEntries();
			}
		}
	}

	private rebuildEntries(): void {

		this.entries = [];

		let monPolicy: MonitorPolicy = this.editor.Policy;
		monPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {

			let monEntry: MonitorEntry = monPolicy.DeviceMonitorMap.GetMonitor_DeviceGuid(deviceGuid);
			monEntry.Settings.GetEntries().ForEach((monSetsEntry: MonitorSettingsEntry) => {

				let deviceGuidStr = ValueEncoder.EncodeGuid(deviceGuid);
				let monSetsEntryGuidStr = ValueEncoder.EncodeGuid(monSetsEntry.Guid);

				let device: Device = null;
				let state: string = "";
				let stateDetails: string = "";
				let inOutMbps: string = "";

				if (!!this.deviceCache[deviceGuidStr]) {
					device = this.deviceCache[deviceGuidStr];
				}

				if (!!this.stateCache[monSetsEntryGuidStr]) {
					let monState: MonitorStateDto = this.stateCache[monSetsEntryGuidStr];
					state = monState.monitorSettingsEntryState;
					stateDetails = monState.monitorSettingsEntryStateDetails;
					inOutMbps = monState.inOutMbps;
				}

				let monSetsSnmpIfEntry = <MonitorSettings_SnmpIfEntry>(monSetsEntry);

				let entry = new SnmpIfMonitorListEntry(deviceGuidStr, device, monSetsEntryGuidStr, monSetsSnmpIfEntry,
					state, stateDetails, inOutMbps);
				this.entries.push(entry);
			});
		});
	}

	private getDevices(deviceGuids: List<string>): Observable<any> {

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, "monitor-network", deviceGuids, false).subscribe(
				(result: GetDeviceResultDto) => {
					result.Devices.ForEach((value: Device) => {
						let deviceGuidStr = ValueEncoder.EncodeGuid(value.GUID);
						this.deviceCache[deviceGuidStr] = value;
					});

					subject.next(true);
				},
				(error) => {
					subject.error(false);
				});

		return subject.asObservable();
	}


	private getMonitorState(policy: MonitorPolicy): Observable<any> {

		this.applyTempChangesIfValid.emit();

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.monService.getMonitorState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, policy, false).subscribe(
				(result: GetMonitorStateResultDto) => {
					result.entries.forEach((entry: MonitorStateDto) => {
						this.stateCache[entry.monitorSettingsEntryGuid] = entry;
					});

					subject.next(true);
				},
				(error) => {
					subject.error(false);
				});

		return subject.asObservable();
	}

	public BuildForm(): void {
		let useHostnames = false;
		if (this.editor) {
			let monPolicy: MonitorPolicy = this.editor.Policy;
			let monSettings: MonitorSettings_SnmpIf = <MonitorSettings_SnmpIf>monPolicy.Settings;

			useHostnames = monSettings.PollByHostname;
		}

		this.UseHostnames = new UntypedFormControl(useHostnames);

		this.SettingsForm = this.fb.group({
			UseHostnames: this.UseHostnames,
		});
	}

	public applyTempChanges(): void {
		if (!!this.editor && this.editor.HasPolicy) {

			let monPolicy: MonitorPolicy = this.editor.Policy;
			let monSettings: MonitorSettings_SnmpIf = <MonitorSettings_SnmpIf>monPolicy.Settings;

			monSettings.PollByHostname = this.UseHostnames.value;
			this.editor.HasChanges = true;

			this.BuildForm();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["Devices"];
	}

	public addDevice(): void {
		this.deviceSelectorVisible = true;
	}

	public editDevice(entry: SnmpIfMonitorListEntry): void {
		this.setInitialInterfaceSelection(entry.deviceGuid);
		this.targetEntry = entry;
		this.interfaceSelectorVisible = true;
	}

	private setInitialInterfaceSelection(deviceGuid: string): void {

		this.initialInterfaceSelection = [];

		this.getAllEntriesForDevice(deviceGuid).forEach((devEntry: SnmpIfMonitorListEntry) => {
			this.initialInterfaceSelection.push(devEntry.settingsEntryTag.IfIndex);
		});
	}

	private getAllEntriesForDevice(deviceGuid: string): SnmpIfMonitorListEntry[] {

		let deviceEntries: SnmpIfMonitorListEntry[] = [];

		this.entries.forEach((entry: SnmpIfMonitorListEntry) => {
			if (entry.deviceGuid === deviceGuid) {
				deviceEntries.push(entry);
			}
		});

		return deviceEntries;
	}

	public deleteDevice(entry: SnmpIfMonitorListEntry): void {

		let deviceEntries: SnmpIfMonitorListEntry[] = this.getAllEntriesForDevice(entry.deviceGuid);

		deviceEntries.forEach((devEntry: SnmpIfMonitorListEntry) => {
			let b = this.editor.Remove_SnmpIfEntry(devEntry.deviceTag, devEntry.settingsEntryTag);
			console.assert(b);
		});

		this.rebuildEntries();
	}

	public refreshEntries(): void {
		this.retrieveDevicesAndMonitorStates();
	}

	public onDeviceSelectorOk(result: MonitorDeviceSelectorResult): void {

		let devicesToAdd = new List<string>();
		result.devices.forEach((value: DisplayDeviceListEntryDto) => {
			let deviceGuid = Guid.FromString(value.deviceGuid);
			if (!this.editor.Policy.DeviceGuids.Contains(deviceGuid)) {
				devicesToAdd.Add(value.deviceGuid);
			}
		});

		if (devicesToAdd.Count > 0) {
			this.getDevices(devicesToAdd).subscribe(
				(gdResult: any) => {
					let addedDeviceCount = 0;
					let deviceGuidToSelect: string = "";

					devicesToAdd.ForEach((deviceGuidStr: string) => {
						let device: Device = this.deviceCache[deviceGuidStr];
						if (!!device) {
							if (this.editor.AddSnmpIfMonitors(device)) {
								++addedDeviceCount;
								deviceGuidToSelect = deviceGuidStr;
							}
						}
					});

					if (addedDeviceCount > 0) {
						// Make sure new monitor entries have GUIDs
						this.editor.Policy.VerifyGuids();

						this.getMonitorState(this.editor.Policy).subscribe(
							(gmsResult: any) => {
								this.rebuildEntries();
							},
							(gmsError) => {
								this.rebuildEntries();
								this.messageService.displayErrorMessage("Could not retrieve monitor state info");
							});
					}

					if (addedDeviceCount < devicesToAdd.Count) {
						this.messageService.displayErrorMessage("Error adding device(s)");
					}
				},
				(gdError) => {
					this.messageService.displayErrorMessage("Could not retrieve monitor device info");
				});
		}
		else {
			this.messageService.displayInfoMessage("No new devices were added");
		}
	}

	public onDeviceSelectorCancel(): void {
		return;
	}

	public onInterfaceSelectorOk(selectedInterfaces: number[]): void {

		if (this.targetEntry) {

			let changes = false;

			// Add interfaces
			selectedInterfaces.forEach((ifIndex: number) => {
				if (!ArrayUtils.Contains(this.initialInterfaceSelection, ifIndex)) {
					this.editor.AddSnmpIfMonitor(this.targetEntry.deviceTag, ifIndex);
					changes = true;
				}
			});

			// Remove interfaces
			this.getAllEntriesForDevice(this.targetEntry.deviceGuid).forEach((devEntry: SnmpIfMonitorListEntry) => {
				if (!ArrayUtils.Contains(selectedInterfaces, devEntry.ifIndex)) {
					this.editor.Remove_SnmpIfEntry(this.targetEntry.deviceTag, devEntry.settingsEntryTag);
					changes = true;
				}
			});

			if (changes) {
				// Make sure new monitor entries have GUIDs
				this.editor.Policy.VerifyGuids();

				this.rebuildEntries();
			}
		}

		this.targetEntry = null;
		this.initialInterfaceSelection = [];
		//this.interfaceSelectorVisible = false;
	}

	public onInterfaceSelectorCancel(): void {

		this.targetEntry = null;
		this.initialInterfaceSelection = [];
		//this.interfaceSelectorVisible = false;
	}

	public doCustomSort(event): void {
		let comparer = function (a: SnmpIfMonitorListEntry, b: SnmpIfMonitorListEntry): number {
			let result = 0;

			switch (event.field) {
				case "ipAddress":
					if (a.ipAddressTag && b.ipAddressTag) {
						result = a.ipAddressTag.CompareTo(b.ipAddressTag);
					}
					else if (a.ipAddressTag) {
						result = 1;
					}
					else if (b.ipAddressTag) {
						result = -1;
					}
					break;
				case "ifIndex":
					if (a.hasIfIndexTag && b.hasIfIndexTag) {
						if (a.ifIndexTag < b.ifIndexTag) {
							result = -1;
						}
						else if (a.ifIndexTag > b.ifIndexTag) {
							result = 1;
						}
						else {
							result = 0;
						}
					}
					else if (a.hasIfIndexTag) {
						result = 1;
					}
					else if (b.hasIfIndexTag) {
						result = -1;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].toString().localeCompare(b[event.field].toString());
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

}
